import { Dayjs } from "dayjs";
import { Policy, Address, PhoneNumber, Driver, Vehicle } from "../objects/policy";
import { v4 as uuidv4 } from 'uuid';
import { method } from "lodash";
import { UpdateQuoteByPropertyRequest } from "../objects/UpdateQuoteByPropertyRequest";
import { AddToBillingRequest } from "../objects/addToBillingRequest";

export class QuoteService {
    issuePolicyApi = "";
    requestEsignatureApi = "";
    postPaymentApi = "";
    getRatecycleApi = "";
    serializeApi = "";
    setDefaultsApi = "";
    saveQuoteApi = "";
    updateQuoteApi = "";
    addToBillingApi = "";
    getDefaultsApi = "";

    constructor (){
        var baseApi = process.env.REACT_APP_BASE_API_URL;
        var api = process.env.REACT_APP_API_QUOTE_ISSUE ?? "";
        var postPaymentApiUrl = process.env.REACT_APP_API_QUOTE_POSTPAYMENT ?? "";
        var getRatecycleApiUrl = process.env.REACT_APP_API_QUOTE_GETRATECYCLE ?? "";
        var serializeUrl = process.env.REACT_APP_API_QUOTE_SERIALIZE ?? "";
        var setDefaultsUrl = process.env.REACT_APP_API_QUOTE_SETDEFAULTS ?? "";
        var saveQuoteUrl = process.env.REACT_APP_API_QUOTE_SAVE ?? "";
        var updateQuoteUrl = process.env.REACT_APP_API_QUOTE_UPDATE ?? "";
        var requestEsignatureUrl = process.env.REACT_APP_API_QUOTE_REQUEST_ESIG ?? "";
        var addToBillingUrl = process.env.REACT_APP_API_QUOTE_ADD_TO_BILLING ?? "";
        var getDefaultsUrl = process.env.REACT_APP_API_QUOTE_GETDEFAULTS ?? "";
        
        this.issuePolicyApi = baseApi + api;
        this.postPaymentApi = baseApi + postPaymentApiUrl;
        this.getRatecycleApi = baseApi + getRatecycleApiUrl;
        this.serializeApi = baseApi + serializeUrl;
        this.setDefaultsApi = baseApi + setDefaultsUrl;
        this.saveQuoteApi = baseApi + saveQuoteUrl;
        this.updateQuoteApi = baseApi + updateQuoteUrl;
        this.requestEsignatureApi = baseApi + requestEsignatureUrl;
        this.addToBillingApi = baseApi + addToBillingUrl;
        this.getDefaultsApi = baseApi + getDefaultsUrl;
    }

    public updateMobilePhoneNumber(quote: Policy, mobilePhoneNum: string){
        var clonePolicy = {...quote};
        
        var clonePhoneNumbers = (clonePolicy.phoneNumbers ?? []).map(item=>({...item}));
        var mobilePhoneIndex = (clonePhoneNumbers?.findIndex(m=> m.type === "Mobile") ?? -1);
        
        if(mobilePhoneIndex === -1){
            var mobilePhone = mobilePhoneIndex === -1 ? new PhoneNumber() : (clonePhoneNumbers ?? [])[(mobilePhoneIndex ?? 0)];
            mobilePhone ={...mobilePhone, type: "Mobile", number: mobilePhoneNum}
            
            clonePolicy.phoneNumbers?.push(mobilePhone);
        }else{
            for(let item of (clonePolicy?.phoneNumbers ?? [])){
                if(item.type === "Mobile"){
                    item.number = mobilePhoneNum;
                }
            }
        }

        return clonePolicy;
    }

    public updateDetailInfo(quote: Policy, 
        emailAddress: string, 
        mobilePhoneNum: string, 
        dateOfBirth: Dayjs | undefined | null, 
        gender: string, 
        maritalStatus: string, 
        address: string, 
        city:string, 
        policyState:string, 
        zip:string ) {

        var clonePolicy = {...quote};
        
        clonePolicy.emailAddress = emailAddress;
        
        var clonePhoneNumbers = (clonePolicy.phoneNumbers ?? []).map(item=>({...item}));
        var mobilePhoneIndex = (clonePhoneNumbers?.findIndex(m=> m.type === "Mobile") ?? -1);
        
        if(mobilePhoneIndex === -1){
            var mobilePhone = mobilePhoneIndex === -1 ? new PhoneNumber() : (clonePhoneNumbers ?? [])[(mobilePhoneIndex ?? 0)];
            mobilePhone ={...mobilePhone, type: "Mobile", number: mobilePhoneNum}
            
            clonePolicy.phoneNumbers?.push(mobilePhone);
        }else{
            for(let item of (clonePolicy?.phoneNumbers ?? [])){
                if(item.type === "Mobile"){
                    item.number = mobilePhoneNum;
                }
            }
        }


        var cloneDrivers = (clonePolicy.drivers ?? []).map(item=>({...item}));
        var insuredDriverIndex = (cloneDrivers?.findIndex(m=> m.status === "RATED") ?? -1);
        var dobIso = dateOfBirth?.toISOString() ?? "";

        if(insuredDriverIndex === -1){
            var insuredDriver = new Driver();
            insuredDriver.driverNumber = 1;
            insuredDriver.relation = "INSURED";
            insuredDriver.status = "RATED";
            insuredDriver.dob = new Date(dobIso);
            insuredDriver.firstName = clonePolicy.insuredFirstName;
            insuredDriver.lastName = clonePolicy.insuredLastName;
            insuredDriver.suffix = clonePolicy.insuredSuffix;
            insuredDriver.gender = gender;
            insuredDriver.isMarried = maritalStatus == "M";

            clonePolicy.drivers?.push(insuredDriver);
        }else{
            for(let item of (clonePolicy?.drivers ?? [])){
                if(item.relation === "INSURED"){
                    item.dob = new Date(dobIso);
                    item.gender = gender;
                    item.isMarried = maritalStatus == "M";
                    item.firstName = clonePolicy.insuredFirstName;
                    item.lastName = clonePolicy.insuredLastName;
                    item.suffix = clonePolicy.insuredSuffix;
                }
            }
        }
        
        for(const aitem of (clonePolicy?.addresses ?? [])){
            if(aitem.type === "mailing" || aitem.type === "garaging"){
                aitem.streetName = address;
                aitem.city = city;
                aitem.state = policyState;
                aitem.zip = zip;
                
            }
        }
        
        return clonePolicy;

    }
    
    /**
     * Update basic insured info collected on step 1
     * 
     * @param quote [QuoteObject](https://docs.api.perimetersoftwaresolutions.com/#quotes-rate-quote)
     * @param policyState state in which the quote/policy will be written in
     * @param firstName first name of the designated insured
     * @param lastName last name of the designated insured
     * @param middleInitial middle initial of the designated insured
     * @param suffix suffix of the designated insured
     * @returns Quote object contains updated quote/policy information
     */
    public updateBasic(quote: Policy, 
        effectiveDate: Dayjs | null, 
        policyState: string, 
        firstName:string, 
        lastName:string, 
        middleInitial:string, 
        suffix:string) : Policy {
        var clonePolicy = {...quote};
        var cloneAddresses = (clonePolicy.addresses ?? []).map(item=>({...item}));
        
        var mailingIndex = (cloneAddresses?.findIndex(m=> m.type ==="mailing") ?? -1);
        var garagingIndex = (cloneAddresses?.findIndex(m=> m.type ==="garaging") ?? -1);

        if(mailingIndex === -1){
            
            var mailingAddress = mailingIndex === -1 ? new Address() : (cloneAddresses ?? [])[(mailingIndex ?? 0)];
            mailingAddress ={...mailingAddress, type: "mailing", state: policyState}
            
            clonePolicy.addresses?.push(mailingAddress);
        }else{
            for(let item of (clonePolicy?.addresses ?? [])){
                if(item.type === "mailing"){
                    item.state = policyState
                }
            }
        }

        if(garagingIndex === -1){
            var garagingAddress = garagingIndex === -1 ? new Address() : (cloneAddresses ?? [])[(garagingIndex ?? 0)];
            garagingAddress ={...garagingAddress, type: "garaging", state: policyState}
            
            clonePolicy.addresses?.push(garagingAddress);
        }else{
            for(let item of (clonePolicy?.addresses ?? [])){
                if(item.type === "garaging"){
                    item.state = policyState
                }
            }
        }
        
        var now = new Date();
        var formattedNow = now.getMonth()+1 + "/" + now.getDate() + "/" + now.getFullYear()
        var formattedEffectiveDate = effectiveDate == null ? formattedNow : effectiveDate.toDate().getMonth()+1 + "/" + effectiveDate.toDate().getDate() + "/" + effectiveDate.toDate().getFullYear();


        clonePolicy.effectiveDate = formattedEffectiveDate;
        clonePolicy.insuredFirstName = firstName;
        clonePolicy.insuredLastName = lastName;
        clonePolicy.insuredMiddleInitial = middleInitial;
        clonePolicy.insuredSuffix = suffix;
        clonePolicy.ratedState = policyState;
        
        return clonePolicy;
    }

    public addVehicle(quote: Policy, vehicle: Vehicle){
        var clone = quote ?? new Policy();
    }

    public async serialize(policynumber: string) {
        var url = this.serializeApi.replace("{quotenum}", policynumber);
        var result = await fetch(url,
        {
            mode: "cors",
            method: "GET",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            }
        });


        if(result.ok){
            var policy = (await result.json()) as Policy;
            
            var modifiedVehicles = [...policy.vehicles];
            var deDupedVehicles = modifiedVehicles.filter((original, index, self)=> {
                return index === self.findIndex((target) => (
                    target.vin === original.vin
                  ));
            });

            var modifiedDrivers = [...policy.drivers];
            var deDupedDrivers = modifiedDrivers.filter((original, index, self)=> {
                return index === self.findIndex((target) => (
                    target.firstName === original.firstName && target.lastName == original.lastName
                  ));
            });

            policy.vehicles = deDupedVehicles;
            policy.drivers = deDupedDrivers;

            return {
                ok: true,
                data: policy
            }
        }else{
            return {
                ok: false,
                error: await result.json()
            }
        }
    }

    
    public async serializeEncrypted(policynumber: string, isDecryp: boolean) {
        var url = this.serializeApi.replace("{quotenum}", policynumber);
        var result = await fetch(url +"?isDecrypt=true",
        {
            mode: "cors",
            method: "GET",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            }
        });


        if(result.ok){
            var policy = (await result.json()) as Policy;
            
            var modifiedVehicles = [...policy.vehicles];
            var deDupedVehicles = modifiedVehicles.filter((original, index, self)=> {
                return index === self.findIndex((target) => (
                    target.vin === original.vin
                  ));
            });

            var modifiedDrivers = [...policy.drivers];
            var deDupedDrivers = modifiedDrivers.filter((original, index, self)=> {
                return index === self.findIndex((target) => (
                    target.firstName === original.firstName && target.lastName == original.lastName
                  ));
            });

            policy.vehicles = deDupedVehicles;
            policy.drivers = deDupedDrivers;

            return {
                ok: true,
                data: policy
            }
        }else{
            return {
                ok: false,
                error: await result.json()
            }
        }
    }

    public issuePolicy(quote: Policy) {
        var data = {
            policyNumber: quote.policyNumber,
            emailAddress: quote.emailAddress,
            mobilePhoneNumber: ""
        };

        var jsonRequest = JSON.stringify(data);

        return fetch(this.issuePolicyApi,
        {
            mode: "cors",
            method: "POST",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            },
            body: jsonRequest
        });
    }

    public requestEsignature(quote: Policy, methodToSign: string = "EMAIL") {
        var data = {
            policyNumber: quote.policyNumber,
            esignatureOption: methodToSign
        };

        var jsonRequest = JSON.stringify(data);

        return fetch(this.requestEsignatureApi,
        {
            mode: "cors",
            method: "POST",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            },
            body: jsonRequest
        });
    }

    public postPayment(policyNumber: string, 
        emailAddress: string,
        downPayment: number, 
        transactionId: string, 
        transactionType: string, 
        lastFour: number, 
        token:string, 
        expirationDate: string) {

            var paymentInfo: any[] = [];

            paymentInfo.push({
                transactionType: "DOWNPAY",
                creditCardLastFour: "1111",
                paymentToken: uuidv4(),
                creditCardExpirationDate: "2021-05-01",
                paymentType: "CC"
            });

            paymentInfo.push({
                transactionType: "RECURRING",
                creditCardLastFour: "1111",
                paymentToken: uuidv4(),
                creditCardExpirationDate: "2021-05-01",
                paymentType: "CC"
            });

            
        var data = {
            guid: uuidv4(),
            policyNumber: policyNumber,
            totalPaidAmt: 56.33,
            transactionId: transactionId,
            paymentDetails: paymentInfo,
            paymentType: "CC",
            username: emailAddress
        };

        var jsonRequest = JSON.stringify(data);
        
        return fetch(this.postPaymentApi,
        {
            mode: "cors",
            method: "POST",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            },
            body: jsonRequest
        });
    }

    public setDefaultCoverages(policyNumber: string) {
        var url = this.setDefaultsApi.replace("{quotenum}", policyNumber);

        return fetch(url,
        {
            mode: "cors",
            method: "POST",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            }
        });
    }

    
    async getDefaultCoverages(policyNumber: string, type:string) {
        var url = this.getDefaultsApi.replace("{quotenum}", policyNumber)
        .replace("{type}", type);

        var response = await fetch(url,
        {
            mode: "cors",
            method: "GET",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            }
        });


        if(response.ok){
            return {
                ok: true,
                data: await response.json()
            }
        }else{
            return {
                ok: false,
                error: await response.json()
            }
        }
    }

    public getRateCycle(policyState: string, effectiveDate: string) {
        var effDate = new Date(effectiveDate).toLocaleDateString();
        var url = this.getRatecycleApi.replace("{state}", policyState)
                                      .replace("{effectiveDate}", effDate.replace("/", "-").replace("/", "-"));

        return fetch(url,
        {
            mode: "cors",
            method: "GET",
            headers: {
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json",
                "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
            }
        });
    
    }

    async save(quote: Policy) {
        var jsonRequest = JSON.stringify(quote);

        var response = await fetch(this.saveQuoteApi,
            {
                mode: "cors",
                method: "POST",
                headers: {
                    "Access-Control-Allow-Origin": "*",
                    "Content-Type": "application/json",
                    "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
                },
                body: jsonRequest
            });

        if(response.ok){
            return {
                ok: true,
                data: await response.json()
            }
        }else{
            return {
                ok: false,
                error: await response.json()
            }
        }
    }

    
    async update(update: UpdateQuoteByPropertyRequest) {
        var jsonRequest = JSON.stringify(update);

        var response = await fetch(this.updateQuoteApi,
            {
                mode: "cors",
                method: "PATCH",
                headers: {
                    "Access-Control-Allow-Origin": "*",
                    "Content-Type": "application/json",
                    "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
                },
                body: jsonRequest
            });

        if(response.ok){
            return {
                ok: true,
                data: await response.json()
            }
        }else{
            return {
                ok: false,
                error: await response.json()
            }
        }
    }

    async addToBilling(request: AddToBillingRequest) {
        var jsonRequest = JSON.stringify(request);

        var response = await fetch(this.addToBillingApi,
            {
                mode: "cors",
                method: "POST",
                headers: {
                    "Access-Control-Allow-Origin": "*",
                    "Content-Type": "application/json",
                    "pace-api-key": (process.env.REACT_APP_API_KEY ?? "")
                },
                body: jsonRequest
            });

        if(response.ok){
            return {
                ok: true,
                data: await response.json()
            }
        }else{
            return {
                ok: false,
                error: await response.json()
            }
        }
    }
}